'use client';

import {
  type CmsWorkspaceLaunchDetail,
  normalizeCmsWorkspaceLaunchDetail,
} from './workspace-prefill-contract';

export type { CmsWorkspaceLaunchDetail } from './workspace-prefill-contract';

export const CMS_WORKSPACE_LAUNCH_EVENT = 'cms:workspace-launch';

type ReplayLaunchDetail = CmsWorkspaceLaunchDetail & {
  __cmsClientWorkspaceReplay?: true;
};

export type CmsWorkspaceActivation = {
  source: 'launch';
  detail: CmsWorkspaceLaunchDetail;
};

type StoredWorkspaceActivation = CmsWorkspaceActivation & {
  href: string;
};

let currentWorkspaceActivation: StoredWorkspaceActivation | null = null;

function canUseBrowserRuntime() {
  return typeof window !== 'undefined';
}

function getCurrentHref() {
  return canUseBrowserRuntime() ? window.location.href : '';
}

function resolveActivationHref(targetHref?: string) {
  return targetHref
    ? new URL(targetHref, getCurrentHref()).href
    : getCurrentHref();
}

function toLaunchDetail(event: Event) {
  return ((event as CustomEvent<ReplayLaunchDetail>).detail ?? {}) || {};
}

export function dispatchCmsWorkspaceLaunch(
  detail: CmsWorkspaceLaunchDetail,
  options: { targetHref?: string } = {}
) {
  if (!canUseBrowserRuntime()) {
    return;
  }

  const normalizedDetail = normalizeCmsWorkspaceLaunchDetail(detail);
  const activationHref = resolveActivationHref(options.targetHref);

  currentWorkspaceActivation = {
    source: 'launch',
    detail: normalizedDetail,
    href: activationHref,
  };

  if (activationHref !== getCurrentHref()) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<CmsWorkspaceLaunchDetail>(CMS_WORKSPACE_LAUNCH_EVENT, {
      detail: normalizedDetail,
    })
  );
}

export function takeCurrentCmsWorkspaceActivation() {
  if (
    !canUseBrowserRuntime() ||
    !currentWorkspaceActivation ||
    currentWorkspaceActivation.href !== getCurrentHref()
  ) {
    return null;
  }

  const activation: CmsWorkspaceActivation = {
    source: currentWorkspaceActivation.source,
    detail: currentWorkspaceActivation.detail,
  };
  currentWorkspaceActivation = null;
  return activation;
}

export function replayCmsWorkspaceLaunch(detail: CmsWorkspaceLaunchDetail) {
  if (!canUseBrowserRuntime()) {
    return;
  }

  const normalizedDetail = normalizeCmsWorkspaceLaunchDetail(detail);

  window.dispatchEvent(
    new CustomEvent<ReplayLaunchDetail>(CMS_WORKSPACE_LAUNCH_EVENT, {
      detail: {
        ...normalizedDetail,
        __cmsClientWorkspaceReplay: true,
      },
    })
  );
}

export function subscribeCmsWorkspaceLaunch(
  handler: (detail: CmsWorkspaceLaunchDetail) => void,
  options: {
    includeReplays?: boolean;
  } = {}
) {
  if (!canUseBrowserRuntime()) {
    return () => {};
  }

  const handleWorkspaceLaunch = (event: Event) => {
    const rawDetail = toLaunchDetail(event);
    const detail = normalizeCmsWorkspaceLaunchDetail(rawDetail);
    if (rawDetail.__cmsClientWorkspaceReplay && !options.includeReplays) {
      return;
    }

    handler(detail);
  };

  window.addEventListener(CMS_WORKSPACE_LAUNCH_EVENT, handleWorkspaceLaunch);

  return () => {
    window.removeEventListener(
      CMS_WORKSPACE_LAUNCH_EVENT,
      handleWorkspaceLaunch
    );
  };
}

export function subscribeCmsWorkspaceActivation(
  handler: (activation: CmsWorkspaceActivation) => void,
  options: {
    emitCurrent?: boolean;
    includeReplays?: boolean;
  } = {}
) {
  if (!canUseBrowserRuntime()) {
    return () => {};
  }

  const handleWorkspaceLaunch = (event: Event) => {
    const rawDetail = toLaunchDetail(event);
    const detail = normalizeCmsWorkspaceLaunchDetail(rawDetail);
    if (rawDetail.__cmsClientWorkspaceReplay && !options.includeReplays) {
      return;
    }

    handler({
      source: 'launch',
      detail,
    });
  };

  window.addEventListener(CMS_WORKSPACE_LAUNCH_EVENT, handleWorkspaceLaunch);

  if (
    options.emitCurrent &&
    currentWorkspaceActivation &&
    currentWorkspaceActivation.href === getCurrentHref()
  ) {
    handler({
      source: currentWorkspaceActivation.source,
      detail: currentWorkspaceActivation.detail,
    });
  }

  return () => {
    window.removeEventListener(
      CMS_WORKSPACE_LAUNCH_EVENT,
      handleWorkspaceLaunch
    );
  };
}

export function resetCmsWorkspaceSessionForTests() {
  currentWorkspaceActivation = null;
}
